import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import CarinaChevron from 'common/icons/CarinaChevron';
import { GuestBrandedDefaultColors } from 'common/compounds/GuestBrandedTheme/constants';

// This component is for Guest side usage (should not be used in Host side pages). It's a styled component to look like
// Carina components.
// NOTE: This component could be further improved by using styled-component or Radix-ui
const GuestBrandedAccordion = ({
    title,
    isOpen: controlledIsOpen,
    defaultOpen = false,
    isDisabled = false,
    onToggle,
    children,
    style = {},
}) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalOpen;
    const [isFocusVisible, setIsFocusVisible] = useState(false);

    const hoverBackground = `var(--guest-branded-textHover, ${GuestBrandedDefaultColors.textHover})`;
    const focusOutline = `var(--guest-branded-action, ${GuestBrandedDefaultColors.action})`;

    useEffect(() => {
        const handleMouseDown = () => setIsFocusVisible(false);
        const handleKeyDown = (e) => {
            if (e.key === 'Tab') setIsFocusVisible(true);
        };

        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const [hover, setHover] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const contentRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(isOpen ? 'none' : '0px');

    useEffect(() => {
        if (isOpen && contentRef.current) {
            setMaxHeight(`${contentRef.current.scrollHeight}px`);
        } else {
            setMaxHeight('0px');
        }
    }, [isOpen]);

    const toggle = () => {
        if (isDisabled) return;
        if (!isControlled) setInternalOpen(!isOpen);
        if (onToggle) onToggle(!isOpen);
    };

    const headerStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px 8px 8px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.4 : 1,
        backgroundColor: hover ? hoverBackground : 'transparent',
        outline: isFocused && isFocusVisible ? `2px solid ${focusOutline}` : '2px solid transparent',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        fontSize: '1.125rem',
    };

    const chevronStyle = {
        display: 'flex',
        alignItems: 'center',
        transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
        marginRight: '8px',
        transition: 'transform 0.2s ease',
    };

    const sectionStyle = {
        boxSizing: 'border-box',
        ...style,
    };

    const contentStyle = {
        padding: '0 8px 8px 8px',
        fontWeight: 400,
        fontSize: '0.875rem',
        overflow: 'hidden',
        transition: 'max-height 0.2s ease-in-out',
        maxHeight,
        visibility: isOpen ? 'visible' : 'hidden',
    };

    return (
        <div style={sectionStyle}>
            <div
                style={{ ...headerStyle, alignItems: 'center' }}
                role="button"
                aria-expanded={isOpen}
                tabIndex={isDisabled ? -1 : 0}
                onClick={toggle}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onKeyDown={(e) => {
                    if (!isDisabled && e.key === 'Enter') {
                        toggle();
                    }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            >
                <span style={chevronStyle}>
                    <CarinaChevron width={24} height={24} />
                </span>
                <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {title}
                </div>
            </div>
            <div ref={contentRef} style={contentStyle}>
                {children}
            </div>
        </div>
    );
};

GuestBrandedAccordion.propTypes = {
    title: PropTypes.node.isRequired,
    isOpen: PropTypes.bool,
    defaultOpen: PropTypes.bool,
    isDisabled: PropTypes.bool,
    onToggle: PropTypes.func,
    children: PropTypes.node.isRequired,
    style: PropTypes.object,
};

export default GuestBrandedAccordion;
